import type {
  JWT,
  ClaimResponse,
  PhoneConfirmationRequest,
  RegistrationRequest,
  ConfirmRegistrationByEmailRequest,
  LoginByEmailRequest,
  LoginByPhoneRequest,
  ClaimAndPasswordRequest,
  ResolvedShare
} from '@bimdata/definitions/dto';

import api, { pub } from './api';

type RegistrationClaim = {
  id: string;
  phoneTtl: number;
}

async function registration(body: RegistrationRequest): Promise<RegistrationClaim> {
  const { data, headers } = await pub.post<string>('/auth/register', body);
  return {
    id: data,
    phoneTtl: parseInt(headers['bimdata-phone-claim-ttl'] || '0'),
  };
}

async function confirmByPhone({ claim, code }: PhoneConfirmationRequest): Promise<void> {
  const { data } = await pub.post<JWT>('/auth/register/confirm/phone', { claim, code });
  await api.openSession(data);
}

async function confirmByEmail({
  code,
  claim
}: ConfirmRegistrationByEmailRequest): Promise<void> {
  const { status, data } = await pub.post<JWT | null>('/auth/register/confirm', { code, claim });
  if (status == 200 && data) {
    await api.openSession(data);
  }
}

async function login(body: LoginByEmailRequest | LoginByPhoneRequest): Promise<void | string> {
  const { data } = await pub.post<JWT | ClaimResponse>('/auth/login', body);
  if ((data as JWT).access && (data as JWT).refresh) {
    await api.openSession(data as JWT);
    return;
  }
  return (data as ClaimResponse).claim;
}

async function confirmLoginByPhone(body: PhoneConfirmationRequest): Promise<void> {
  const { data } = await pub.post<JWT>('/auth/login/confirm', body);
  await api.openSession(data);
}

async function startExternalPasswordReset(email: string): Promise<void> {
  await pub.post('/auth/pwd/claim', { email });
}

async function checkPasswordResetClaim(code: string): Promise<void> {
  await pub.get('/auth/pwd/claim/check', { params: { code } });
}

async function resetPassword(body: ClaimAndPasswordRequest): Promise<void> {
  const { status, data } = await pub.put<JWT | Record<string, never>>('/auth/pwd/set', body);
  if (status === 200 && data.access && data.refresh) {
    await api.openSession(data as JWT);
  }
}

async function loginByInvitation(code: string, beforeSessionOpenCallback?: () => Promise<void>): Promise<void> {
  const { data } = await pub.post<JWT>('/auth/login/by-invitation', { code });
  beforeSessionOpenCallback && await beforeSessionOpenCallback();
  await api.openSession(data);
  return;
}

async function loginByEmailConfirmation(code: string, beforeSessionOpenCallback?: () => Promise<void>): Promise<void> {
  const { data } = await pub.post<JWT>('/auth/login/by-email-confirm', { code });
  beforeSessionOpenCallback && await beforeSessionOpenCallback();
  await api.openSession(data);
  return;
}

async function registrationByInvitation(body: ClaimAndPasswordRequest): Promise<void> {
  const { data } = await pub.post<JWT>('/auth/register/by-invitation', body);
  await api.openSession(data);
  return;
}

async function loginByShare(code: string): Promise<ResolvedShare> {
  const { data } = await pub.post<ResolvedShare>('/auth/login/by-share', { code });
  return data;
}

export {
  registration,
  confirmByPhone,
  confirmByEmail,
  login,
  confirmLoginByPhone,
  resetPassword,
  loginByInvitation,
  loginByEmailConfirmation,
  registrationByInvitation,
  startExternalPasswordReset,
  checkPasswordResetClaim,
  loginByShare,
};
